import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, formatDistanceToNow } from 'date-fns';
import type { DateRange } from '../../types';
import { computeDuplicationStats } from '../../utils/duplication';
import { useOverviewSystemHealth, usePublishOverviewMetrics } from '../overviewMetrics';
import {
  CHART_AXIS_TICK,
  CHART_ERROR,
  CHART_GRID_PROPS,
  CHART_SUCCESS,
  CHART_SUCCESS_LIGHT,
  CHART_SUCCESS_TEXT,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from './chartTheme';

/**
 * Normalized job shape consumed by the analytics view. Every product's
 * history row (lightning `HistoricalJob`, video/interior/ads `Job`)
 * carries these fields, so each Overview supplies a thin adapter that
 * maps its rows into this shape.
 */
export interface AnalyticsJob {
  id: string;
  scan_id?: string | null;
  created_at: string;
  status: string;
  duration_seconds: number | null;
  error: string | null;
}

interface RequestAnalyticsProps {
  /** Per-product fetcher pulling every job in the given window. */
  fetchJobs: (range: DateRange) => Promise<AnalyticsJob[]>;
  /** Optional target for the "View full statistics" link. */
  statsHref?: string;
}

type RangeKey = '24h' | '7d' | '30d';
type IntervalKey = '15m' | '1h' | '6h' | '1d';

const RANGE_LABELS: Record<RangeKey, string> = {
  '24h': 'Last 24h',
  '7d': '7 Days',
  '30d': '30 Days',
};

const INTERVAL_LABELS: Record<IntervalKey, string> = {
  '15m': '15 min',
  '1h': '1 hour',
  '6h': '6 hours',
  '1d': '1 day',
};

const INTERVAL_MS: Record<IntervalKey, number> = {
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '6h': 6 * 60 * 60_000,
  '1d': 24 * 60 * 60_000,
};

/** Sensible default granularity for each range. */
const DEFAULT_INTERVAL: Record<RangeKey, IntervalKey> = {
  '24h': '1h',
  '7d': '6h',
  '30d': '1d',
};

function rangeToDateRange(key: RangeKey): DateRange {
  const to = new Date();
  const from = new Date(to);
  switch (key) {
    case '24h':
      from.setHours(from.getHours() - 24);
      break;
    case '7d':
      from.setDate(from.getDate() - 7);
      break;
    case '30d':
      from.setDate(from.getDate() - 30);
      break;
  }
  return { from, to };
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function slowestBarStyle(duration: number, p90: number, maxDuration: number) {
  const width = maxDuration > 0 ? Math.max(8, (duration / maxDuration) * 100) : 0;
  const relativeToP90 = p90 > 0 ? duration / p90 : duration / Math.max(maxDuration, 1);
  const p90Intensity = Math.min(Math.max((relativeToP90 - 0.5) / 0.65, 0), 1);
  const startOpacity = 0.45 + p90Intensity * 0.3;
  const endOpacity = 0.6 + p90Intensity * 0.4;

  return {
    width: `${width}%`,
    background: `linear-gradient(90deg, rgba(96, 165, 250, ${startOpacity}), rgba(37, 99, 235, ${endOpacity}))`,
  };
}

/** p-th percentile (0..1) of an ascending-sorted numeric array. */
function percentile(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  const idx = Math.ceil(p * sortedAsc.length) - 1;
  const clamped = Math.min(Math.max(idx, 0), sortedAsc.length - 1);
  return sortedAsc[clamped];
}

interface CountBucket {
  t: number;
  label: string;
  completed: number;
  failed: number;
  total: number;
}

function SystemHealthPanel() {
  const health = useOverviewSystemHealth();
  if (!health) return null;

  const rows = [
    {
      label: 'API',
      value: health.apiHealthy ? 'Healthy' : 'Unavailable',
      tone: health.apiHealthy ? 'healthy' : 'unhealthy',
    },
    {
      label: 'Database',
      value:
        health.databaseHealthy == null
          ? 'Not reported'
          : health.databaseHealthy
            ? 'Healthy'
            : 'Unavailable',
      tone:
        health.databaseHealthy == null
          ? 'neutral'
          : health.databaseHealthy
            ? 'healthy'
            : 'unhealthy',
    },
    {
      label: 'Storage',
      value:
        health.storageHealthy == null
          ? 'Not reported'
          : health.storageHealthy
            ? 'Healthy'
            : 'Unavailable',
      tone:
        health.storageHealthy == null
          ? 'neutral'
          : health.storageHealthy
            ? 'healthy'
            : 'unhealthy',
    },
  ] as const;

  const toneClasses = {
    healthy: { dot: 'bg-emerald-500', text: 'text-emerald-700' },
    unhealthy: { dot: 'bg-red-500', text: 'text-red-700' },
    neutral: { dot: 'bg-slate-300', text: 'text-slate-500' },
  };

  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-soft">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">System health</h3>
        <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 text-blue-500">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h4l2.5-7 5 14 2.5-7h4" />
          </svg>
        </span>
      </div>
      <ul className="space-y-3">
        {rows.map((row) => {
          const tone = toneClasses[row.tone];
          return (
            <li key={row.label} className="flex items-center gap-2 text-xs">
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${tone.dot}`} />
              <span className="text-slate-700">{row.label}</span>
              <span className={`ml-auto font-medium tabular-nums ${tone.text}`}>{row.value}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function RequestAnalytics({ fetchJobs, statsHref }: RequestAnalyticsProps) {
  const publishOverviewMetrics = usePublishOverviewMetrics();
  const [rangeKey, setRangeKey] = useState<RangeKey>('24h');
  const [interval, setInterval] = useState<IntervalKey>('1h');
  const [jobs, setJobs] = useState<AnalyticsJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refetch whenever the range changes; interval changes only recompute
  // client-side, so they don't trigger a network round-trip.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchJobs(rangeToDateRange(rangeKey))
      .then((result) => {
        if (!cancelled) setJobs(result);
      })
      .catch((ex) => {
        if (!cancelled) {
          setError(ex instanceof Error ? ex.message : 'Failed to load analytics');
          setJobs([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchJobs, rangeKey]);

  const handleRangeChange = useCallback((key: RangeKey) => {
    setLoading(true);
    setError(null);
    setRangeKey(key);
    setInterval(DEFAULT_INTERVAL[key]);
  }, []);

  const buckets = useMemo<CountBucket[]>(() => {
    const step = INTERVAL_MS[interval];
    const range = rangeToDateRange(rangeKey);
    if (!range.from || !range.to) return [];

    const start = Math.floor(range.from.getTime() / step) * step;
    const end = range.to.getTime();
    const labelFmt =
      step >= INTERVAL_MS['1d']
        ? 'MMM d'
        : rangeKey === '24h'
          ? 'HH:mm'
          : 'M/d HH:mm';

    const map = new Map<number, { completed: number; failed: number }>();
    for (let t = start; t <= end; t += step) {
      map.set(t, { completed: 0, failed: 0 });
    }

    for (const job of jobs) {
      const ts = new Date(job.created_at).getTime();
      if (Number.isNaN(ts)) continue;
      const bucket = Math.floor(ts / step) * step;
      const entry = map.get(bucket);
      if (!entry) continue;
      if (job.status === 'failed') entry.failed += 1;
      else entry.completed += 1;
    }

    return Array.from(map.entries()).map(([t, v]) => ({
      t,
      label: format(new Date(t), labelFmt),
      completed: v.completed,
      failed: v.failed,
      total: v.completed + v.failed,
    }));
  }, [jobs, rangeKey, interval]);

  const totalRequests = useMemo(
    () => buckets.reduce((sum, b) => sum + b.total, 0),
    [buckets],
  );

  const recentErrors = useMemo(
    () =>
      jobs
        .filter((j) => j.status === 'failed')
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )
        .slice(0, 20),
    [jobs],
  );

  const { p90, slowest, hasDuration } = useMemo(() => {
    const completed = jobs.filter(
      (j) => j.status === 'completed' && j.duration_seconds != null,
    );
    const durations = completed
      .map((j) => j.duration_seconds as number)
      .sort((a, b) => a - b);
    const slowestJobs = [...completed]
      .sort(
        (a, b) =>
          (b.duration_seconds as number) - (a.duration_seconds as number),
      )
      .slice(0, 10);
    return {
      p90: percentile(durations, 0.9),
      slowest: slowestJobs,
      hasDuration: durations.length > 0,
    };
  }, [jobs]);

  const successRate = useMemo(() => {
    const completed = jobs.filter((job) => job.status === 'completed').length;
    const failed = jobs.filter((job) => job.status === 'failed').length;
    const finished = completed + failed;
    return finished > 0 ? (completed / finished) * 100 : null;
  }, [jobs]);

  const duplicatePercent = useMemo(
    () => computeDuplicationStats(jobs).duplicatePercent,
    [jobs],
  );
  const errorCount = useMemo(
    () => jobs.filter((job) => job.status === 'failed').length,
    [jobs],
  );

  useEffect(() => {
    if (!publishOverviewMetrics) return;
    publishOverviewMetrics({
      successRate,
      p90Duration: hasDuration ? p90 : null,
      duplicatePercent,
      errorCount: loading || error ? null : errorCount,
      rangeLabel: RANGE_LABELS[rangeKey],
    });
  }, [
    duplicatePercent,
    error,
    errorCount,
    hasDuration,
    loading,
    p90,
    publishOverviewMetrics,
    rangeKey,
    successRate,
  ]);

  useEffect(
    () => () => {
      publishOverviewMetrics?.(null);
    },
    [publishOverviewMetrics],
  );

  return (
    <div className="space-y-4">
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft">
        {/* Header + controls */}
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:px-5">
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-900">
              Request analytics
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Request counts, recent errors and slowest requests
            </p>
          </div>
          <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto">
            {/* Range selector */}
            <div className="flex w-full overflow-hidden rounded-lg border border-slate-200 sm:w-auto">
              {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
                <button
                  key={key}
                  onClick={() => handleRangeChange(key)}
                  className={`flex-1 px-3 py-1 text-xs font-medium transition-colors sm:flex-none ${
                    rangeKey === key
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {RANGE_LABELS[key]}
                </button>
              ))}
            </div>
            {/* Interval selector */}
            <select
              value={interval}
              onChange={(e) => setInterval(e.target.value as IntervalKey)}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              title="Bucket interval"
            >
              {(Object.keys(INTERVAL_LABELS) as IntervalKey[]).map((key) => (
                <option key={key} value={key}>
                  {INTERVAL_LABELS[key]}
                </option>
              ))}
            </select>
            {statsHref && (
              <Link
                to={statsHref}
                className="flex items-center gap-1 text-xs font-medium text-indigo-600 transition-colors hover:text-indigo-700"
              >
                Full stats
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            )}
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700 sm:mx-5">
            {error}
          </div>
        )}

        {/* Request counts — full width */}
        <div className="px-4 pb-4 pt-3 sm:px-5 sm:pb-5">
          <div className="mb-3 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
              Request counts
            </h3>
            <div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 sm:w-auto sm:justify-end">
              <div
                className="flex items-center gap-3 text-[11px] text-slate-500"
                aria-label="Chart legend"
              >
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-success-500" />
                  Completed
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-danger-500" />
                  Failed
                </span>
              </div>
              <div className="text-right">
                <span className="text-lg font-semibold text-slate-800">{totalRequests}</span>
                <span className="ml-1 text-[10px] uppercase text-slate-500">total</span>
              </div>
            </div>
          </div>
          <div
            data-testid="request-counts-chart"
            className="h-64 md:h-[clamp(6rem,calc(100dvh-44rem),16rem)]"
          >
            {loading && jobs.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-slate-500">
                Loading analytics…
              </div>
            ) : totalRequests > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={buckets}
                  margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
                  barCategoryGap="18%"
                >
                  <defs>
                    <linearGradient id="request-completed-gradient" x1="0" y1="100%" x2="0" y2="0%">
                      <stop offset="0%" stopColor={CHART_SUCCESS_LIGHT} stopOpacity={0.62} />
                      <stop offset="100%" stopColor={CHART_SUCCESS} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...CHART_GRID_PROPS} />
                  <XAxis
                    dataKey="label"
                    tick={CHART_AXIS_TICK}
                    interval="preserveStartEnd"
                    minTickGap={24}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={CHART_AXIS_TICK}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                  />
                  <Bar
                    dataKey="completed"
                    name="Completed"
                    stackId="a"
                    fill="url(#request-completed-gradient)"
                    radius={[2, 2, 0, 0]}
                    maxBarSize={16}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey="failed"
                    name="Failed"
                    stackId="a"
                    fill={CHART_ERROR}
                    radius={[2, 2, 0, 0]}
                    maxBarSize={16}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-slate-400">
                No requests in this period
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Recent errors + system health + slowest requests */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Recent errors */}
        <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-soft">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Recent errors</h3>
            <div className="text-right">
              <span className="text-lg font-bold" style={{ color: CHART_ERROR }}>
                {recentErrors.length}
              </span>
              <span className="ml-1 text-[10px] text-slate-500 uppercase">shown</span>
            </div>
          </div>
          {recentErrors.length > 0 ? (
            <ul className="space-y-2 overflow-y-auto max-h-64 pr-1">
              {recentErrors.map((job) => (
                <li
                  key={job.id}
                  className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[11px] text-slate-500 truncate">
                      {job.id.slice(0, 8)}
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0">
                      {formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-700 line-clamp-2 break-words">
                    {job.error || 'Unknown error'}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <div
              className="flex-1 flex items-center justify-center text-sm"
              style={{ color: CHART_SUCCESS_TEXT }}
            >
              No errors in this period
            </div>
          )}
        </div>

        <SystemHealthPanel />

        {/* Slowest requests (p90) */}
        <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-soft">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Slowest requests</h3>
              <p className="mt-0.5 text-[11px] text-slate-400">By processing time (P90)</p>
            </div>
            <div className="text-right">
              <span className="text-lg font-bold text-slate-700">
                {p90 > 0 ? formatDuration(p90) : '—'}
              </span>
              <span className="ml-1 text-[10px] text-slate-500 uppercase">p90</span>
            </div>
          </div>
          {slowest.length > 0 ? (
            <ul className="max-h-64 space-y-1 overflow-y-auto pr-1">
              {slowest.map((job) => {
                const duration = job.duration_seconds as number;
                const maxDuration = slowest[0]?.duration_seconds as number;
                const p90Difference = duration - p90;
                const comparison =
                  p90Difference > 0
                    ? `${formatDuration(p90Difference)} above p90`
                    : p90Difference < 0
                      ? `${formatDuration(Math.abs(p90Difference))} below p90`
                      : 'At p90';

                return (
                  <li
                    key={job.id}
                    className="grid grid-cols-[minmax(4.5rem,auto)_minmax(5rem,1fr)_auto] items-center gap-3 py-2"
                  >
                    <span className="truncate font-mono text-[11px] text-slate-600">
                      {job.id.slice(0, 8)}
                    </span>
                    <div className="h-1.5 min-w-0 overflow-hidden rounded-full bg-slate-100">
                      <div
                        role="progressbar"
                        aria-label={`${job.id.slice(0, 8)} processing time`}
                        aria-valuemin={0}
                        aria-valuemax={maxDuration}
                        aria-valuenow={duration}
                        className="h-full rounded-full"
                        style={slowestBarStyle(duration, p90, maxDuration)}
                        title={comparison}
                      />
                    </div>
                    <span
                      className="shrink-0 text-xs font-medium tabular-nums text-slate-700"
                      title={comparison}
                    >
                      {formatDuration(duration)}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
              No completed requests in this period
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
